import React, { useRef, useState } from 'react';
import { styled } from '@mui/material';
import { ArrowBack, CloudUpload, Close } from '@mui/icons-material';
import { Colors } from 'library';
import { RBaseButton } from './RBaseButton';
import { RCard } from './RCard';
import { RErrorText } from './RErrorText';
import { RIconButton } from './RIconButton';
import { RLabel } from './RLabel';
import { RLink } from './RLink';
import { RModal, RModalFooter } from './RModal';
import { RPillButton } from './RPillButton';
import { RRow } from './RRow';
import { RStack } from './RStack';
import { RText } from './RText';
import { RTitle } from './RTitle';
import { RToast } from './RToast';

interface RDocumentUploadProps {
    documentTypes: string | string[];
    button: React.ReactElement;
    onSubmit: (file: File, typeName: string) => Promise<void>;
    successMessage?: string;
    errorMessage?: string;
    acceptedFileTypes?: string;
    maxFileSizeMB?: number;
    selectTitle?: string;
    uploadTitle?: string;
    description?: string;
}

const DEFAULT_ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
const DEFAULT_MAX_FILE_SIZE_MB = 10;

export function RDocumentUpload({
    documentTypes,
    button,
    onSubmit,
    successMessage = 'Document uploaded',
    errorMessage = 'Could not upload document',
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    selectTitle = 'Select Document Type',
    uploadTitle,
    description,
}: RDocumentUploadProps) {
    const types = Array.isArray(documentTypes) ? documentTypes : [documentTypes];
    const isSingle = !Array.isArray(documentTypes);
    const [open, setOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    function showToast(message: string) {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    }

    function openModal() {
        setOpen(true);
        setSelectedType(isSingle ? types[0] : null);
    }

    function closeModal() {
        setOpen(false);
        setSelectedType(null);
    }

    async function handleSubmit(file: File): Promise<boolean> {
        if (!selectedType) return false;
        try {
            await onSubmit(file, selectedType);
        } catch {
            showToast(errorMessage);
            return false;
        }
        closeModal();
        showToast(successMessage);
        return true;
    }

    const trigger = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            button.props.onClick?.(e);
            openModal();
        },
    });

    return (
        <>
            {trigger}
            {open && !selectedType && (
                <RModal title={selectTitle} onClose={closeModal} width={520}>
                    <RStack gap="none">
                        {types.map((type) => (
                            <RCard key={type} padding="xs" onClick={() => setSelectedType(type)}>
                                <RText weight="semibold">{type}</RText>
                            </RCard>
                        ))}
                    </RStack>
                </RModal>
            )}
            {open && selectedType && (
                <RModal title={uploadTitle ?? `Upload ${selectedType}`} onClose={closeModal} width={560}>
                    <UploadForm
                        selectedType={selectedType}
                        onChangeType={isSingle ? null : () => setSelectedType(null)}
                        onCancel={closeModal}
                        onSubmit={handleSubmit}
                        acceptedFileTypes={acceptedFileTypes}
                        maxFileSizeMB={maxFileSizeMB}
                        description={description}
                    />
                </RModal>
            )}
            <RToast message={toast} />
        </>
    );
}

interface UploadFormProps {
    selectedType: string;
    onChangeType: (() => void) | null;
    onCancel: () => void;
    onSubmit: (file: File) => Promise<boolean>;
    acceptedFileTypes: string;
    maxFileSizeMB: number;
    description?: string;
}

function UploadForm({
    selectedType,
    onChangeType,
    onCancel,
    onSubmit,
    acceptedFileTypes,
    maxFileSizeMB,
    description,
}: UploadFormProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const acceptedDisplay = acceptedFileTypes
        .split(',')
        .map((t) => t.replace('.', '').toUpperCase())
        .join(', ');

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const next = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (next && next.size > maxFileSizeBytes) {
            setError(`File is too large. Maximum size is ${maxFileSizeMB}MB.`);
            return;
        }
        setError(null);
        setFile(next);
    }

    async function handleUpload() {
        if (!file) return;
        setUploading(true);
        setError(null);
        await onSubmit(file);
        setUploading(false);
    }

    return (
        <RStack gap="md">
            {onChangeType && (
                <RLink
                    onClick={onChangeType}
                    weight="semibold"
                    underline={false}
                    color={Colors.turquoise}
                    icon={<ArrowBack sx={{ fontSize: 18 }} />}
                >
                    Back
                </RLink>
            )}

            <RCard tone="muted" padding="xs">
                <RText weight="medium" tone="secondary">
                    {selectedType}
                </RText>
            </RCard>

            <RStack gap="none">
                <RLabel>
                    Upload Document <RText tone="error">*</RText>
                </RLabel>
                {description && (
                    <RText size="sm" tone="secondary">
                        {description}
                    </RText>
                )}
                {file ? (
                    <RCard padding="sm">
                        <RRow align="center" gap="sm">
                            <RStack grow gap="none">
                                <RText weight="semibold">{file.name}</RText>
                                <RText size="sm" tone="secondary">
                                    {formatBytes(file.size)}
                                </RText>
                            </RStack>
                            <RIconButton
                                icon={Close}
                                onClick={() => setFile(null)}
                                disabled={uploading}
                                size={28}
                                iconSize={18}
                            />
                        </RRow>
                    </RCard>
                ) : (
                    <DropZone onClick={() => inputRef.current?.click()}>
                        <CloudUpload sx={{ fontSize: 32, color: Colors.mediumGrey }} />
                        <RTitle size="h3">Click to choose a file</RTitle>
                        <RText tone="secondary" size="sm">
                            {acceptedDisplay} up to {maxFileSizeMB}MB
                        </RText>
                    </DropZone>
                )}
                <input ref={inputRef} type="file" accept={acceptedFileTypes} hidden onChange={handleFileChange} />
            </RStack>

            {error && <RErrorText>{error}</RErrorText>}

            <RModalFooter>
                <RPillButton variant="secondary" onClick={onCancel} disabled={uploading}>
                    Cancel
                </RPillButton>
                <RPillButton onClick={handleUpload} disabled={!file || uploading} color={Colors.turquoise}>
                    {uploading ? 'Uploading...' : 'Upload'}
                </RPillButton>
            </RModalFooter>
        </RStack>
    );
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DropZone = styled(RBaseButton)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '48px 24px',
    border: `1.5px dashed ${Colors.disabledGrey}`,
    borderRadius: 12,
    backgroundColor: Colors.faintGrey,
    '&:hover': {
        borderColor: Colors.mediumTurq,
        backgroundColor: Colors.lightTurq,
    },
    '&:focus, &:focus-visible': {
        borderColor: Colors.mediumTurq,
    },
});
